// EnhancedPlatVisualization.tsx
// Enhanced Plat Visualization with Area Analysis and Builder Information

import React, { useEffect, useRef, useState } from 'react';
import { PlatData, PlatLot, platDataService } from '../services/PlatDataService';
import { PlatOverlay } from '../utils/map/PlatOverlay';

interface AreaDiscrepancy {
  calculatedArea: number;
  reportedArea: number;
  difference: number;
  percentageDifference: number;
  possibleReasons: string[];
}

interface BuilderInformation {
  lotNumber: string;
  block?: string;
  area: {
    gross: number;
    net: number;
    buildable: number;
    unit: string;
  };
  dimensions: {
    frontage: number;
    depth: number;
    perimeter: number;
  };
  setbacks: {
    front: number;
    rear: number;
    side: number;
    corner?: number;
  };
  restrictions: {
    maxCoverage: number;
    minSquareFootage?: number;
    maxHeight?: number;
  };
  utilities: {
    water: boolean;
    sewer: boolean;
    electric: boolean;
    gas: boolean;
  };
  zoning?: string;
  floodZone?: string;
  easements: Array<{
    type: string;
    area: number;
  }>;
  buildableEnvelope: {
    area: number;
    percentage: number;
  };
}

export const EnhancedPlatVisualization: React.FC<{
  address?: string;
  coordinates?: { lat: number; lng: number };
  calculatedArea?: number;
  reportedArea?: number;
}> = ({ address, coordinates, calculatedArea, reportedArea }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [platOverlay, setPlatOverlay] = useState<PlatOverlay | null>(null);
  const [platData, setPlatData] = useState<PlatData | null>(null);
  const [selectedLot, setSelectedLot] = useState<PlatLot | null>(null);
  const [builderInfo, setBuilderInfo] = useState<BuilderInformation | null>(null);
  const [areaDiscrepancy, setAreaDiscrepancy] = useState<AreaDiscrepancy | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(true);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const mapInstance = new google.maps.Map(mapRef.current, {
      zoom: 18,
      center: coordinates || { lat: 33.7490, lng: -84.3880 }, // Default to Atlanta
      mapTypeId: 'hybrid',
      tilt: 0,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true
    });

    setMap(mapInstance);
    const overlay = new PlatOverlay(mapInstance);
    setPlatOverlay(overlay);

    return () => {
      overlay.destroy();
    };
  }, [coordinates]);

  // Load plat data
  useEffect(() => {
    if (!map || !platOverlay) return;

    const loadPlatData = async () => {
      try {
        let data: PlatData | null = null;
        
        if (coordinates) {
          data = await platDataService.fetchPlatByCoordinates(
            coordinates.lat,
            coordinates.lng,
            'fulton', // Default county
            'ga'
          );
        } else if (address) {
          data = await platDataService.fetchPlatByAddress(
            address,
            'fulton',
            'ga'
          );
        }

        if (data) {
          setPlatData(data);
          platOverlay.loadPlatData(data);
          
          // Select first lot by default
          if (data.lots.length > 0) {
            handleLotSelection(data.lots[0]);
          }
        }
      } catch (error) {
        console.error('Error loading plat data:', error);
      }
    };

    loadPlatData();
  }, [map, platOverlay, coordinates, address]);

  // Calculate area discrepancy
  useEffect(() => {
    // Only calculate if we have actual data
    if (!platData && !calculatedArea && !reportedArea) return;
    
    // Use actual plat data area or provided values
    const calcArea = calculatedArea || platData?.area || 0;
    const repArea = reportedArea || (platData?.lots?.[0]?.area) || 0;
    
    if (calcArea === 0 || repArea === 0) return;

    const difference = calcArea - repArea;
    const percentageDiff = ((difference / repArea) * 100);

    const possibleReasons = [];
    
    if (percentageDiff > 15) {
      possibleReasons.push('Calculation may include non-buildable areas (easements, setbacks)');
      possibleReasons.push('Different measurement standards (gross vs net area)');
      possibleReasons.push('Includes right-of-way or common areas');
    }
    
    if (Math.abs(percentageDiff) > 10) {
      possibleReasons.push('Different survey methods or dates');
      possibleReasons.push('Rounding differences in conversion (metric to imperial)');
    }
    
    if (calcArea > repArea) {
      possibleReasons.push('Calculated area may include lot extensions or modifications');
      possibleReasons.push('May include utility easements in total area');
    } else {
      possibleReasons.push('Reported area may be lot area only (excluding setbacks)');
      possibleReasons.push('May exclude irregular boundary sections');
    }

    setAreaDiscrepancy({
      calculatedArea: calcArea,
      reportedArea: repArea,
      difference,
      percentageDifference: percentageDiff,
      possibleReasons
    });
  }, [calculatedArea, reportedArea, platData]);

  // Handle lot selection
  const handleLotSelection = (lot: PlatLot) => {
    setSelectedLot(lot);
    platOverlay?.selectLot(lot);

    // Calculate builder information - only use actual data
    const grossArea = lot.area || platData?.area || calculatedArea || 0;
    
    if (grossArea === 0) {
      console.log('No area data available for lot:', lot.lotNumber);
      return;
    }
    
    const info: BuilderInformation = {
      lotNumber: lot.lotNumber,
      block: lot.block,
      area: {
        gross: grossArea,
        net: grossArea * 0.85, // Estimate 85% net
        buildable: grossArea * 0.60, // Estimate 60% buildable
        unit: 'sq ft'
      },
      dimensions: {
        frontage: lot.frontage || 150,
        depth: lot.depth || 200,
        perimeter: calculatePerimeter(lot.boundary)
      },
      setbacks: platData?.setbacks || {
        front: 25,
        rear: 20,
        side: 10,
        corner: 15
      },
      restrictions: {
        maxCoverage: 40, // 40% lot coverage typical
        minSquareFootage: 2000,
        maxHeight: 35
      },
      utilities: {
        water: lot.utilities?.includes('water') || true,
        sewer: lot.utilities?.includes('sewer') || true,
        electric: lot.utilities?.includes('electric') || true,
        gas: lot.utilities?.includes('gas') || false
      },
      zoning: lot.zoning || 'R-1 (Residential)',
      floodZone: lot.floodZone || 'X (Minimal Risk)',
      easements: lot.easements?.map(e => ({
        type: e,
        area: 500 // Estimate
      })) || [],
      buildableEnvelope: calculateBuildableEnvelope(lot, platData?.setbacks)
    };

    setBuilderInfo(info);
  };

  // Calculate perimeter
  const calculatePerimeter = (boundary: Array<{lat: number; lng: number}>): number => {
    if (boundary.length < 2) return 0;
    
    let perimeter = 0;
    for (let i = 0; i < boundary.length; i++) {
      const j = (i + 1) % boundary.length;
      const distance = calculateDistance(boundary[i], boundary[j]);
      perimeter += distance;
    }
    return Math.round(perimeter);
  };

  // Calculate distance between two points
  const calculateDistance = (
    p1: {lat: number; lng: number},
    p2: {lat: number; lng: number}
  ): number => {
    // Haversine formula for distance
    const R = 20902231; // Earth radius in feet
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLon = (p2.lng - p1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Calculate buildable envelope
  const calculateBuildableEnvelope = (lot: PlatLot, setbacks?: any): {
    area: number;
    percentage: number;
  } => {
    const grossArea = lot.area || platData?.area || calculatedArea || 0;
    
    if (grossArea === 0) {
      return { area: 0, percentage: 0 };
    }
    
    const setbackArea = calculateSetbackArea(lot, setbacks);
    const easementArea = lot.easements ? lot.easements.length * 500 : 0; // Estimate
    
    const buildableArea = grossArea - setbackArea - easementArea;
    const percentage = (buildableArea / grossArea) * 100;
    
    return {
      area: Math.round(buildableArea),
      percentage: Math.round(percentage)
    };
  };

  // Calculate setback area
  const calculateSetbackArea = (lot: PlatLot, setbacks?: any): number => {
    if (!setbacks) return 0;
    
    const frontage = lot.frontage || 150;
    const depth = lot.depth || 200;
    
    // Approximate setback area calculation
    const frontSetbackArea = frontage * (setbacks.front || 25);
    const rearSetbackArea = frontage * (setbacks.rear || 20);
    const sideSetbackArea = 2 * depth * (setbacks.side || 10);
    
    // Avoid double counting corners
    const cornerAdjustment = 2 * (setbacks.front || 25) * (setbacks.side || 10) +
                            2 * (setbacks.rear || 20) * (setbacks.side || 10);
    
    return frontSetbackArea + rearSetbackArea + sideSetbackArea - cornerAdjustment;
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 p-4 bg-gray-50 min-h-screen">
      {/* Map Container */}
      <div className="flex-1 relative">
        <div 
          ref={mapRef} 
          className="w-full h-[600px] rounded-lg shadow-lg border-2 border-gray-200"
        />
        
        {/* Map Controls */}
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-2 space-y-2">
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
          >
            {showAnalysis ? 'Hide' : 'Show'} Analysis
          </button>
        </div>

        {/* Area Discrepancy Banner */}
        {areaDiscrepancy && (
          <div className="absolute bottom-4 left-4 right-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-yellow-800">Area Discrepancy Detected</h3>
              <span className="text-2xl">⚠️</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Calculated Area:</p>
                <p className="font-bold text-lg">{calculatedArea.toFixed(2)} sq ft</p>
              </div>
              <div>
                <p className="text-gray-600">Reported Area:</p>
                <p className="font-bold text-lg">{reportedArea.toFixed(2)} sq ft</p>
              </div>
              <div>
                <p className="text-gray-600">Difference:</p>
                <p className="font-bold text-lg text-yellow-700">
                  {areaDiscrepancy.difference.toFixed(2)} sq ft
                  <span className="text-sm ml-1">
                    ({areaDiscrepancy.percentageDifference > 0 ? '+' : ''}{areaDiscrepancy.percentageDifference.toFixed(1)}%)
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analysis Panel */}
      {showAnalysis && (
        <div className="lg:w-[450px] space-y-4">
          {/* Builder Information Card */}
          {builderInfo && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">
                Builder Information
              </h2>
              
              {/* Lot Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-600">
                  Lot {builderInfo.lotNumber} {builderInfo.block ? `- Block ${builderInfo.block}` : ''}
                </h3>
                
                {/* Area Breakdown */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Area Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Gross Area:</span>
                      <span className="font-bold">{builderInfo.area.gross.toFixed(0)} {builderInfo.area.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Net Area:</span>
                      <span className="font-bold">{builderInfo.area.net.toFixed(0)} {builderInfo.area.unit}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span>Buildable Area:</span>
                      <span className="font-bold text-green-600">
                        {builderInfo.area.buildable.toFixed(0)} {builderInfo.area.unit}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dimensions */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Dimensions</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="text-center">
                      <p className="text-gray-500">Frontage</p>
                      <p className="font-bold text-lg">{builderInfo.dimensions.frontage}'</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500">Depth</p>
                      <p className="font-bold text-lg">{builderInfo.dimensions.depth}'</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500">Perimeter</p>
                      <p className="font-bold text-lg">{builderInfo.dimensions.perimeter}'</p>
                    </div>
                  </div>
                </div>

                {/* Setbacks */}
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Required Setbacks</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span>Front:</span>
                      <span className="font-bold">{builderInfo.setbacks.front} ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Rear:</span>
                      <span className="font-bold">{builderInfo.setbacks.rear} ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Side:</span>
                      <span className="font-bold">{builderInfo.setbacks.side} ft</span>
                    </div>
                    {builderInfo.setbacks.corner && (
                      <div className="flex justify-between">
                        <span>Corner:</span>
                        <span className="font-bold">{builderInfo.setbacks.corner} ft</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Building Restrictions */}
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Building Restrictions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Max Lot Coverage:</span>
                      <span className="font-bold">{builderInfo.restrictions.maxCoverage}%</span>
                    </div>
                    {builderInfo.restrictions.minSquareFootage && (
                      <div className="flex justify-between">
                        <span>Min House Size:</span>
                        <span className="font-bold">{builderInfo.restrictions.minSquareFootage} sq ft</span>
                      </div>
                    )}
                    {builderInfo.restrictions.maxHeight && (
                      <div className="flex justify-between">
                        <span>Max Height:</span>
                        <span className="font-bold">{builderInfo.restrictions.maxHeight} ft</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Utilities */}
                <div className="bg-green-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Available Utilities</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {Object.entries(builderInfo.utilities).map(([utility, available]) => (
                      <div key={utility} className="flex items-center">
                        <span className={`mr-2 text-lg ${available ? 'text-green-500' : 'text-gray-400'}`}>
                          {available ? '✅' : '❌'}
                        </span>
                        <span className="capitalize">{utility}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Zoning & Flood Zone */}
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Zoning Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Zoning:</span>
                      <span className="font-bold">{builderInfo.zoning}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Flood Zone:</span>
                      <span className="font-bold">{builderInfo.floodZone}</span>
                    </div>
                  </div>
                </div>

                {/* Buildable Envelope */}
                <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Buildable Envelope</h4>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {builderInfo.buildableEnvelope.area.toLocaleString()} sq ft
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {builderInfo.buildableEnvelope.percentage}% of total lot area
                    </p>
                  </div>
                  <div className="mt-3 bg-white bg-opacity-50 rounded p-2">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-400 to-blue-400"
                        style={{ width: `${builderInfo.buildableEnvelope.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Area Discrepancy Analysis */}
          {areaDiscrepancy && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Area Discrepancy Analysis</h3>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700">Possible Reasons:</h4>
                <ul className="space-y-2">
                  {areaDiscrepancy.possibleReasons.map((reason, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span className="text-sm text-gray-600">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-semibold text-blue-800">Builder Recommendation:</p>
                <p className="text-sm text-blue-700 mt-1">
                  Use the reported area ({reportedArea.toFixed(0)} sq ft) for official documentation 
                  and the buildable area ({(reportedArea * 0.6).toFixed(0)} sq ft) for planning purposes. 
                  Always verify with current survey documents.
                </p>
              </div>
            </div>
          )}

          {/* Lot Selection */}
          {platData && platData.lots.length > 1 && (
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Select Lot</h3>
              <div className="grid grid-cols-3 gap-2">
                {platData.lots.map((lot) => (
                  <button
                    key={lot.lotNumber}
                    onClick={() => handleLotSelection(lot)}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      selectedLot?.lotNumber === lot.lotNumber
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Lot {lot.lotNumber}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedPlatVisualization;